import * as React from "react";
import * as yup from "yup";
import * as _ from "lodash";
import {
  Formik,
  FormikValues
} from "formik";
import styled from "styled-components";

import { ITerminology } from "src/library/utils/string-manipulations/tokenize-string";
import { sentenceCase } from "src/library/utils/string-manipulations";
import { Text } from "src/library/components/Text";
import { Form } from "src/library/components/Forms";
import { Modal } from "src/library/components/Modal";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  BulkOrderActionsType,
  PreBulkProductReplaceParams,
  ordersApi
} from "src/core/orders/api/orders.api";
import { OrderQuickProductAdd } from "src/core/products/components/ProductSearchComponent/OrderQuickProductAdd";
import { OrdersTableProductSelectItem } from "src/core/products/components/ProductSearchComponent/OrdersTableProductSelectItem";
import { IProduct } from "src/core/products/api/products.api";

interface IProps {
  terminology: ITerminology;
  show: boolean;
  loading: boolean;
  onConfirm: (formValues: FormikValues, toProduct?: IProduct | null) => void;
  handleDismiss: () => void;
  type: BulkOrderActionsType | null;
  productReplaceFrom?: IProduct | null;
  bulkActionIds: number[];
}

interface TypeModalConfigurationOptions {
  title: string;
  body: string;
  actionsNegative: string;
  actionsAffirmative: string;
  disabledAffirmative: boolean;
  submitForm: () => void;
  content: JSX.Element;
}
interface PreReplaceProductInfo {
  selectedBranches: number;
  eligibleBranches: number;
  selectedOrders: number;
  eligibleOrders: number;
}

/**
 * This component handles the content displayed in a modal when a bulk order action is triggered.
 *
 * The modal content is dependent on the bulk action type. The content is shared by some bulk
 * actions though.
 *
 */
const BulkOrdersAdminUpdateStatusModal: React.ReactFCWithChildren<IProps> = ({
  productReplaceFrom,
  terminology,
  show,
  loading,
  onConfirm,
  handleDismiss,
  type,
  bulkActionIds
}) => {
  const formRef = React.useRef<Formik>(null);
  const [ selectedReplacementProduct, setSelectedReplacementProduct ] = React.useState<IProduct | null>(null);
  const [ preReplaceProductError, setPreReplaceProductError ] = React.useState<string | null>(null);
  const [ preReplaceProductInfo, setPreReplaceProductInfo ] = React.useState<PreReplaceProductInfo | null>(null);

  const [ preBulkProductReplaceResponse, preBulkProductReplace ] = ordersApi<PreReplaceProductInfo, PreBulkProductReplaceParams>(
    "PRE_BULK_REPLACE_PRODUCT"
  );

  // On preBulkProductReplaceResponse
  React.useEffect(() => {
    if (preBulkProductReplaceResponse.error) {
      // Show the error returned
      setPreReplaceProductError(preBulkProductReplaceResponse.error.message);
    } else if (preBulkProductReplaceResponse.data) {
      setPreReplaceProductInfo(preBulkProductReplaceResponse.data);
    }
  }, [ preBulkProductReplaceResponse ]);

  const clearSelectedProduct = React.useCallback(() => {
    setSelectedReplacementProduct(null);
    setPreReplaceProductError(null);
    setPreReplaceProductInfo(null);
  }, [
    setSelectedReplacementProduct,
    setPreReplaceProductError,
    setPreReplaceProductInfo
  ]);

  /**
   * Retrieve the modal configuration depending on the type of bulk operation to perform.
   *
   * If one of the options is not set for a specific bulk action type, the default configuration
   * (set as the configuration key "rest") will be returned.
   */
  const getTypeModalConfigurationOption = React.useCallback(
    <TypeModalConfigurationOptionsKey extends keyof TypeModalConfigurationOptions>(
      option: TypeModalConfigurationOptionsKey,
      toProduct?: IProduct | null
    ): TypeModalConfigurationOptions[TypeModalConfigurationOptionsKey] => {
      const ensuredType = type ? type : "rest";

      const typeModalOptions: Partial<Record<BulkOrderActionsType, Partial<TypeModalConfigurationOptions>>> &
        Record<"rest", TypeModalConfigurationOptions> = {
          [ BulkOrderActionsType.RESYNC ]: {
            title: "Bulk resync",
            body: "Are you sure?",
            actionsAffirmative: `Yes, resync ${terminology.order.plural.text}`,
            actionsNegative: "No, go back",
            content: <></>,
            submitForm: () => onConfirm({})
          },
          [ BulkOrderActionsType.REPLACE_PRODUCT ]: {
            title: productReplaceFrom ?
              `Bulk replace product: ${productReplaceFrom.partNumber} - ${productReplaceFrom.description}` :
              "Please select a product to replace",
            body: toProduct ? `Selected product: ${toProduct.partNumber} - ${toProduct.description}` : "Please select the replacement product",
            actionsNegative: "Cancel",
            content: !productReplaceFrom ? (
              <ReplaceProductInfoWrapper>
                <Text>
                  Error: Please select a product to replace
                </Text>
              </ReplaceProductInfoWrapper>
            ) : toProduct ? (
              preReplaceProductInfo ? (
                <ReplaceProductInfoWrapper>
                  <ol>
                    <li>
                      <Text>
                        {"Total selected orders: "}

                        {preReplaceProductInfo.selectedOrders}
                      </Text>
                    </li>

                    <li>
                      <Text>
                        {"That belongs to "}

                        {preReplaceProductInfo.selectedBranches}

                        {" total branches"}

                      </Text>
                    </li>

                    <li>
                      <Text>
                        {"Total orders eligible to be updated: "}

                        {preReplaceProductInfo.eligibleOrders}
                      </Text>
                    </li>

                    <li>
                      <Text>
                        {"That belongs to "}

                        {preReplaceProductInfo.eligibleBranches}

                        {" total branches"}

                      </Text>
                    </li>
                  </ol>

                  <SelectAnotherProductWrapper onClick={clearSelectedProduct}>
                    Select another product
                  </SelectAnotherProductWrapper>
                </ReplaceProductInfoWrapper>
              ) : preReplaceProductError ? (
                <ReplaceProductInfoWrapper>
                  <Text>
                    Warning some orders may not be able to be updated:
                    {preReplaceProductError}
                  </Text>

                  <SelectAnotherProductWrapper onClick={clearSelectedProduct}>
                    Select another product
                  </SelectAnotherProductWrapper>
                </ReplaceProductInfoWrapper>
              ) : (
                <LoadingSpinner
                  className="loading-spinner"
                  type="circular"
                  position="inline"
                  size={20}
                />
              )
            ) : (
              <OrderQuickProductAdd
                showProductsToggle={false}
                ProductAddItemComponent={OrdersTableProductSelectItem}
                backToProductsButton={false}
                onProductSelect={product => {
                  if (product) {
                    setSelectedReplacementProduct(product);

                    if (!productReplaceFrom) {
                      setPreReplaceProductError("A product to replace must be selected.");
                    } else {
                      preBulkProductReplace({
                        fromProductId: productReplaceFrom.id,
                        toProductId: product.id,
                        orderIds: bulkActionIds
                      });
                    }
                  }
                }}
              />
            ),
            submitForm: () =>
              onConfirm({
                fromProductId: (productReplaceFrom as IProduct).id,
                toProductId: (toProduct as IProduct).id
              }, toProduct),
            disabledAffirmative: toProduct ? false : true
          },
          rest: {
            title: `Bulk ${sentenceCase(terminology.order.singular.text)}(s) Update`,
            body: "Please fillout the relevant fields.",
            actionsAffirmative: `Update ${terminology.order.plural.text}`,
            actionsNegative: "Cancel",
            submitForm: () => (formRef.current ? formRef.current.submitForm() : null),
            content: (
              <Form
                ref={formRef}
                fields={[
                  {
                    type: "textarea",
                    wysiwyg: true,
                    name: "orderNote",
                    label: "Note",
                    validation: yup.string().required()
                  }
                ]}
                onSubmit={formValues => {
                  onConfirm(formValues);
                }}
                submitting={false}
                error={null}
              />
            ),
            disabledAffirmative: false
          }
        };

      return (typeModalOptions[ ensuredType ] as TypeModalConfigurationOptions) &&
        (typeModalOptions[ ensuredType ] as TypeModalConfigurationOptions)[ option ] ?
        (typeModalOptions[ ensuredType ] as TypeModalConfigurationOptions)[ option ] :
        (typeModalOptions.rest as TypeModalConfigurationOptions)[ option ];
    },
    [
      terminology,
      type,
      formRef,
      onConfirm,
      setSelectedReplacementProduct,
      preReplaceProductError,
      preReplaceProductInfo,
      clearSelectedProduct
    ]
  );

  // Clear the product selection on modal close.
  if (!show && selectedReplacementProduct) {
    clearSelectedProduct();
  }

  return (
    <Modal
      small
      type="confirm"
      show={show}
      loading={loading}
      title={getTypeModalConfigurationOption("title", selectedReplacementProduct)}
      body={getTypeModalConfigurationOption("body", selectedReplacementProduct)}
      content={getTypeModalConfigurationOption("content", selectedReplacementProduct)}
      actions={[
        {
          name: getTypeModalConfigurationOption("actionsNegative", selectedReplacementProduct),
          action: () => handleDismiss(),
          $buttonType: "secondary"
        },
        {
          name: getTypeModalConfigurationOption("actionsAffirmative", selectedReplacementProduct),
          action: () => getTypeModalConfigurationOption("submitForm", selectedReplacementProduct)(),
          $buttonType: "primary",
          disabled: getTypeModalConfigurationOption("disabledAffirmative", selectedReplacementProduct)
        }
      ]}
      backgroundAction={() => handleDismiss()}
    />
  );
};

const ReplaceProductInfoWrapper = styled.div`
  display: flex;
  align-items: baseline;
  flex-direction: column;
`;

const SelectAnotherProductWrapper = styled(Text)`
  cursor: pointer;
  text-decoration: underline;
  :hover {
    text-decoration: none;
  }
`;

export default BulkOrdersAdminUpdateStatusModal;
